/**
 * CPR Quick Guide — dispatcher-assisted CPR reference card.
 * Designed for high-stress, scannable use.
 *
 * Sources: Heart & Stroke Foundation Canada 2024, AHA Guidelines 2020.
 */
import { useState, useEffect, useRef } from 'react';
import { X, Volume2, VolumeX, Play, Pause, RotateCcw, HeartPulse, Baby, User, Users, AlertTriangle, Zap, Minimize2, Maximize2, Move, Printer } from 'lucide-react';

const TARGET_BPM = 110;
const BEAT_INTERVAL_MS = (60 / TARGET_BPM) * 1000; // ~545 ms

const SCRIPTS = {
  adulte: {
    label: 'Adulte (puberté et +)',
    icon: User,
    age: '≥ 8 ans',
    verifier: [
      '« Est-il complètement immobile et ne réagit pas si vous lui parlez ? »',
      '« Sa respiration est-elle absente ou irrégulière (gasps, halètements) ? »',
      'Si OUI aux deux → RCR IMMÉDIATEMENT',
    ],
    position: [
      'Allongez la victime sur le dos, sur une surface DURE (sol, pas le lit)',
      'Dégagez la poitrine — retirez ou déchirez les vêtements épais',
      'Mettez-vous à genoux à côté de la victime',
    ],
    technique_hands_only: [
      'Placez le talon d\'une main au CENTRE du thorax (entre les mamelons)',
      'Posez l\'autre main par-dessus, doigts entrelacés',
      'Bras tendus, épaules au-dessus des mains (utiliser le poids du corps)',
      'COMPRIMEZ FORT — 5 à 6 cm de profondeur (sternum vers la colonne)',
      'Laissez la poitrine REMONTER complètement entre chaque compression',
      'Cadence : 100-120/min (≈ 2 par seconde — suivez le métronome)',
      'NE VOUS ARRÊTEZ PAS — continuez sans interruption',
    ],
    technique_with_breaths: [
      '30 compressions rapides et fortes (centre thorax, 5-6 cm)',
      'Basculez la tête en arrière, soulevez le menton',
      'Pincez le nez, soufflez 1 seconde dans la bouche (poitrine se soulève)',
      '2 insufflations, puis recommencez les 30 compressions',
      'Cycle : 30:2 (30 compressions, 2 ventilations)',
      'NE VOUS ARRÊTEZ PAS entre les cycles',
    ],
    dea: [
      'Si quelqu\'un peut chercher le DEA — envoyez-le, ne quittez PAS la victime',
      'Allumez le DEA et SUIVEZ LES INSTRUCTIONS VOCALES',
      'Collez les électrodes sur la peau nue (illustrations sur les pads)',
      'Pendant l\'analyse : NE TOUCHEZ PERSONNE',
      'Si « choc conseillé » → criez « ÉLOIGNEZ-VOUS ! » puis appuyez',
      'Reprenez immédiatement les compressions après le choc',
    ],
    arret: [
      'Les ambulanciers prennent le relais',
      'La victime bouge, ouvre les yeux, respire normalement',
      'Vous êtes physiquement épuisé et un autre prend la relève',
    ],
    encouragement: [
      '« Vous faites EXACTEMENT ce qu\'il faut »',
      '« Continuez, ne vous arrêtez pas, je reste avec vous »',
      '« Suivez le rythme que vous entendez »',
      '« Comptez à voix haute si ça vous aide »',
      '« L\'ambulance arrive dans X minutes »',
    ],
  },
  enfant: {
    label: 'Enfant (1-8 ans)',
    icon: Users,
    age: '1 à 8 ans',
    verifier: [
      '« L\'enfant est-il complètement mou, ne réagit pas si vous le secouez doucement ? »',
      '« Respire-t-il normalement ? »',
      'Si NON → RCR IMMÉDIATEMENT',
    ],
    position: [
      'Allongez l\'enfant sur le dos, surface dure',
      'Dégagez la poitrine',
      'Mettez-vous à côté de lui',
    ],
    technique_hands_only: [
      'UNE seule main (talon de la main) au centre du thorax',
      'Pour enfant plus grand : deux mains comme adulte',
      'Profondeur : ⅓ de l\'épaisseur du thorax (~ 5 cm)',
      'Cadence identique : 100-120/min',
      'Laissez le thorax remonter complètement entre chaque',
    ],
    technique_with_breaths: [
      '30 compressions (1 main, ⅓ thorax)',
      '2 insufflations DOUCES (bouche-à-bouche couvre bouche+nez si possible)',
      'Volume : juste assez pour voir la poitrine se soulever',
      'Cycle : 30:2',
    ],
    dea: [
      'Utilisez les électrodes PÉDIATRIQUES si disponibles',
      'Sinon, électrodes adulte OK (placement antéro-postérieur si possible : 1 sur poitrine, 1 dans le dos)',
      'Suivez les instructions vocales du DEA',
    ],
    arret: [
      'Les ambulanciers arrivent',
      'L\'enfant respire normalement',
      'Vous êtes physiquement incapable de continuer',
    ],
    encouragement: [
      '« Vous sauvez sa vie en ce moment »',
      '« Continuez, ne vous arrêtez pas »',
      '« Le rythme est bon, suivez le bip »',
    ],
  },
  bebe: {
    label: 'Bébé (< 1 an)',
    icon: Baby,
    age: 'moins de 1 an',
    verifier: [
      '« Le bébé est-il complètement mou ? »',
      '« Réagit-il si vous tapez doucement la plante de ses pieds ? »',
      '« Respire-t-il ? »',
      'Si NON → RCR IMMÉDIATEMENT',
    ],
    position: [
      'Allongez le bébé sur le dos, sur une surface dure (table, sol)',
      'Évitez les surfaces molles (matelas, divan)',
      'NE basculez PAS la tête trop loin en arrière (position neutre)',
    ],
    technique_hands_only: [
      'Utilisez DEUX DOIGTS (index + majeur) juste sous la ligne des mamelons',
      'Profondeur : ⅓ de l\'épaisseur du thorax (~ 4 cm)',
      'Cadence : 100-120/min (toujours)',
      'Compressions douces mais fermes',
    ],
    technique_with_breaths: [
      '30 compressions avec 2 doigts',
      '2 insufflations DOUCES : bouche du sauveteur couvre nez + bouche du bébé',
      'Volume très petit : juste un « petit souffle » (joues qui se gonflent un peu)',
      'NE PAS souffler fort (risque pneumothorax)',
      'Cycle : 30:2 si seul, 15:2 si deux sauveteurs formés',
    ],
    dea: [
      'Électrodes PÉDIATRIQUES OBLIGATOIRES si disponibles',
      'Si seulement adulte : placement antéro-postérieur (poitrine + dos)',
      'Suivre instructions vocales',
    ],
    arret: [
      'Les ambulanciers arrivent',
      'Le bébé respire / bouge',
    ],
    encouragement: [
      '« Continuez, vous faites ce qu\'il faut »',
      '« Très doucement mais fermement »',
      '« Ne vous arrêtez pas »',
    ],
  },
};

const Tab = ({ active, onClick, icon: Icon, label, age, color }) => (
  <button
    onClick={onClick}
    className={`flex-1 flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all font-semibold text-sm ${
      active ? 'text-white shadow' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
    }`}
    style={active ? { backgroundColor: color } : {}}
    data-testid={`cpr-tab-${label.toLowerCase().split(' ')[0]}`}
  >
    <Icon className="w-5 h-5 mb-0.5" />
    <span>{label}</span>
    <span className="text-[10px] opacity-75">{age}</span>
  </button>
);

const Block = ({ title, items, color = '#DC2626', icon: Icon }) => (
  <div className="mb-4 break-inside-avoid">
    <div className="flex items-center gap-2 mb-2">
      {Icon && <Icon className="w-4 h-4" style={{ color }} />}
      <h4 className="text-sm font-bold uppercase tracking-wide" style={{ color }}>{title}</h4>
    </div>
    <ol className="space-y-1.5 pl-1">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-sm leading-relaxed">
          <span
            className="flex-shrink-0 w-5 h-5 rounded-full text-white text-[11px] font-bold flex items-center justify-center mt-0.5"
            style={{ backgroundColor: color }}
          >
            {i + 1}
          </span>
          <span className="text-gray-800">{item}</span>
        </li>
      ))}
    </ol>
  </div>
);

const CPRQuickGuide = ({ open, onClose }) => {
  const [tab, setTab] = useState('adulte');
  const [withBreaths, setWithBreaths] = useState(false);
  const [metronomeOn, setMetronomeOn] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const [count, setCount] = useState(0);
  const [pulse, setPulse] = useState(false);
  const [minimized, setMinimized] = useState(false);
  // Floating-panel position (top-right by default, with safety margins)
  const [pos, setPos] = useState(() => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1280;
    return { x: Math.max(20, w - 720), y: 70 };
  });
  // Floating-panel size — user can resize via bottom-right grip
  const [size, setSize] = useState(() => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1280;
    const h = typeof window !== 'undefined' ? window.innerHeight : 800;
    return {
      width: Math.min(700, w - 40),
      height: Math.min(720, h - 90),
    };
  });
  const dragRef = useRef({ active: false, offsetX: 0, offsetY: 0 });
  const resizeRef = useRef({ active: false, startX: 0, startY: 0, startW: 0, startH: 0 });
  const panelRef = useRef(null);
  const intervalRef = useRef(null);
  const audioCtxRef = useRef(null);

  // Reset state when modal closes
  useEffect(() => {
    if (open) return;
    setMetronomeOn(false);
    setCount(0);
  }, [open]);

  // Initialize audio context lazily
  const playBeep = () => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 880; // A5 — clear, non-alarming
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.18, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.09);
    } catch {
      // Silent fail — browser may block before user gesture
    }
  };

  // Metronome loop
  useEffect(() => {
    if (!metronomeOn) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    intervalRef.current = setInterval(() => {
      setCount(c => c + 1);
      setPulse(true);
      setTimeout(() => setPulse(false), 130);
      if (soundOn) playBeep();
    }, BEAT_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [metronomeOn, soundOn]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onClose]);

  // Drag handlers — works for both mouse and touch
  const onDragStart = (e) => {
    if (!panelRef.current) return;
    const isTouch = e.type === 'touchstart';
    const clientX = isTouch ? e.touches[0].clientX : e.clientX;
    const clientY = isTouch ? e.touches[0].clientY : e.clientY;
    const rect = panelRef.current.getBoundingClientRect();
    dragRef.current = {
      active: true,
      offsetX: clientX - rect.left,
      offsetY: clientY - rect.top,
    };
  };
  // Resize handlers — grip on bottom-right corner
  const onResizeStart = (e) => {
    e.stopPropagation();
    const isTouch = e.type === 'touchstart';
    const clientX = isTouch ? e.touches[0].clientX : e.clientX;
    const clientY = isTouch ? e.touches[0].clientY : e.clientY;
    resizeRef.current = {
      active: true,
      startX: clientX,
      startY: clientY,
      startW: size.width,
      startH: size.height,
    };
    if (isTouch) e.preventDefault();
  };
  useEffect(() => {
    if (!open) return;
    const onMove = (e) => {
      const isTouch = e.type === 'touchmove';
      const clientX = isTouch ? e.touches[0].clientX : e.clientX;
      const clientY = isTouch ? e.touches[0].clientY : e.clientY;
      if (resizeRef.current.active) {
        const margin = 8;
        const dx = clientX - resizeRef.current.startX;
        const dy = clientY - resizeRef.current.startY;
        const maxW = window.innerWidth - pos.x - margin;
        const maxH = window.innerHeight - pos.y - margin;
        const newW = Math.max(320, Math.min(maxW, resizeRef.current.startW + dx));
        const newH = Math.max(280, Math.min(maxH, resizeRef.current.startH + dy));
        setSize({ width: newW, height: newH });
        if (isTouch) e.preventDefault();
        return;
      }
      if (!dragRef.current.active) return;
      // Keep panel within viewport
      const margin = 8;
      const w = panelRef.current ? panelRef.current.offsetWidth : 700;
      const h = panelRef.current ? panelRef.current.offsetHeight : 600;
      let x = clientX - dragRef.current.offsetX;
      let y = clientY - dragRef.current.offsetY;
      x = Math.max(margin, Math.min(window.innerWidth - w - margin, x));
      y = Math.max(margin, Math.min(window.innerHeight - h - margin, y));
      setPos({ x, y });
      if (isTouch) e.preventDefault();
    };
    const onEnd = () => {
      dragRef.current.active = false;
      resizeRef.current.active = false;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
  }, [open, pos.x, pos.y]);

  if (!open) return null;

  // Build a printable HTML version of all 3 age groups for instructor handouts.
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const esc = (s) => String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const sectionHtml = (key, label, age, color) => {
      const d = SCRIPTS[key];
      const block = (title, items, c = color) =>
        '<div style="margin:14px 0;page-break-inside:avoid;"><h4 style="font-size:13px;font-weight:bold;color:' + c + ';text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;border-bottom:2px solid ' + c + ';padding-bottom:3px;">' + esc(title) + '</h4>'
        + '<ol style="padding-left:0;list-style:none;">'
        + items.map((it, i) =>
            '<li style="display:flex;gap:8px;padding:3px 0;font-size:12.5px;line-height:1.5;">'
            + '<span style="flex-shrink:0;width:18px;height:18px;border-radius:50%;background:' + c + ';color:white;font-size:10px;font-weight:bold;display:inline-flex;align-items:center;justify-content:center;">' + (i+1) + '</span>'
            + '<span>' + esc(it) + '</span></li>'
          ).join('')
        + '</ol></div>';
      const phrasesBlock =
        '<div style="margin:14px 0;padding:10px;border:2px solid #FCA5A5;background:#FEF2F2;border-radius:8px;page-break-inside:avoid;">'
        + '<h4 style="font-size:12px;font-weight:bold;color:#991B1B;margin-bottom:6px;">🗣️ Phrases à dire au citoyen</h4>'
        + d.encouragement.map(q =>
            '<div style="padding:5px 8px;margin:3px 0;background:white;border-left:3px solid #DC2626;font-size:12px;font-style:italic;color:#7F1D1D;">' + esc(q) + '</div>'
          ).join('')
        + '</div>';
      return '<section style="page-break-after:always;padding:30px 35px;">'
        + '<div style="display:flex;align-items:center;gap:14px;margin-bottom:18px;padding-bottom:12px;border-bottom:3px solid ' + color + ';">'
        + '<div style="width:50px;height:50px;border-radius:12px;background:' + color + ';color:white;font-size:22px;font-weight:bold;display:inline-flex;align-items:center;justify-content:center;">' + esc(label.charAt(0)) + '</div>'
        + '<div><h2 style="font-size:22px;font-weight:bold;color:' + color + ';margin:0;">RCR — ' + esc(label) + '</h2>'
        + '<p style="font-size:12px;color:#64748b;margin:2px 0 0 0;">Tranche d\'âge : ' + esc(age) + ' · Cadence : 100-120 compressions/min · Cycle : 30:2 si ventilation</p></div></div>'
        + block('1. Vérifier la victime', d.verifier)
        + block('2. Position du citoyen', d.position, '#0EA5E9')
        + block('3. Technique — Compressions seulement', d.technique_hands_only)
        + block('4. Technique — Avec ventilation (30:2)', d.technique_with_breaths, '#8B5CF6')
        + phrasesBlock
        + block('5. Utilisation du DEA', d.dea, '#F59E0B')
        + block('6. Quand arrêter la RCR', d.arret, '#16A34A')
        + '</section>';
    };
    const html = '<!DOCTYPE html><html><head><title>Guide RCR — Référence dispatcher</title>'
      + '<style>'
      + '@page { size: letter; margin: 0; }'
      + '* { margin:0; padding:0; box-sizing:border-box; }'
      + 'body { font-family: Segoe UI, Arial, sans-serif; color:#1e293b; }'
      + '.cover { padding:60px 50px; text-align:center; page-break-after:always; background:linear-gradient(135deg,#DC2626,#991B1B);color:white;min-height:100vh;display:flex;flex-direction:column;justify-content:center;}'
      + '.cover h1 { font-size:48px; font-weight:bold; margin-bottom:12px;}'
      + '.cover h2 { font-size:22px; font-weight:300; margin-bottom:40px; opacity:0.9;}'
      + '.cover .badge { display:inline-block;padding:8px 18px;border-radius:20px;background:rgba(255,255,255,0.2);font-size:14px;margin-top:20px;}'
      + '.footer { position:fixed;bottom:10px;left:0;right:0;text-align:center;font-size:10px;color:#94a3b8;}'
      + 'h2 { font-size:20px; }'
      + '</style></head><body>'
      + '<div class="cover">'
      + '<h1>🫀 Guide RCR</h1>'
      + '<h2>Référence dispatcher 911 — Assistance téléphonique</h2>'
      + '<div style="margin:30px auto;padding:20px;background:rgba(255,255,255,0.1);border-radius:12px;max-width:500px;font-size:14px;line-height:1.8;">'
      + '<strong style="font-size:16px;">Cadence universelle :</strong><br>100 à 120 compressions par minute<br><br>'
      + '<strong style="font-size:16px;">Profondeur :</strong><br>Adulte : 5–6 cm · Enfant : ⅓ thorax (~5 cm) · Bébé : ⅓ thorax (~4 cm)<br><br>'
      + '<strong style="font-size:16px;">Cycle :</strong><br>30 compressions : 2 insufflations (si ventilation)'
      + '</div>'
      + '<div class="badge">Lignes directrices Fondation des maladies du cœur — 2024 · AHA Guidelines 2020</div>'
      + '<div style="margin-top:60px;font-size:12px;opacity:0.7;">Simulateur 911 — Référence pédagogique</div>'
      + '</div>'
      + sectionHtml('adulte', 'Adulte (≥ 8 ans)', '8 ans et plus', '#DC2626')
      + sectionHtml('enfant', 'Enfant (1-8 ans)', '1 à 8 ans', '#F59E0B')
      + sectionHtml('bebe', 'Bébé (< 1 an)', 'moins de 1 an', '#3B82F6')
      + '</body></html>';
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 400);
  };

  const data = SCRIPTS[tab];
  const technique = withBreaths ? data.technique_with_breaths : data.technique_hands_only;
  const techniqueTitle = withBreaths ? 'Technique RCR — Avec ventilation (30:2)' : 'Technique RCR — Compressions seulement';

  return (
    <div
      ref={panelRef}
      className="fixed z-[100] bg-white rounded-2xl shadow-2xl border-2 border-red-200 flex flex-col overflow-hidden"
      style={{
        top: pos.y,
        left: pos.x,
        width: minimized ? 340 : size.width,
        height: minimized ? 'auto' : size.height,
      }}
      data-testid="cpr-guide-panel"
    >
      {/* Header — draggable handle */}
      <div
        className="flex items-center justify-between p-3 border-b cursor-move select-none"
        style={{ background: 'linear-gradient(135deg, #DC2626, #B91C1C)' }}
        onMouseDown={onDragStart}
        onTouchStart={onDragStart}
        data-testid="cpr-guide-drag-handle"
      >
        <div className="flex items-center gap-2 text-white">
          <Move className="w-4 h-4 opacity-60" />
          <HeartPulse className="w-6 h-6" />
          <div>
            <h2 className="text-base font-bold leading-tight">Guide RCR</h2>
            {!minimized && <p className="text-[10px] text-red-100">Glissez pour déplacer • Échap pour fermer</p>}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); handlePrint(); }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            className="p-1.5 rounded-lg hover:bg-white/20 text-white"
            data-testid="cpr-guide-print"
            title="Imprimer le guide complet (3 sections)"
          >
            <Printer className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setMinimized(m => !m); }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            className="p-1.5 rounded-lg hover:bg-white/20 text-white"
            data-testid="cpr-guide-minimize"
            title={minimized ? 'Agrandir' : 'Réduire'}
          >
            {minimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            className="p-1.5 rounded-lg hover:bg-white/20 text-white"
            data-testid="cpr-guide-close"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* When minimized: show just the metronome bar for ongoing CPR pacing */}
      {minimized ? (
        <div className="px-3 py-2 flex items-center gap-3 bg-gray-900 text-white" data-testid="cpr-metronome">
          <button
            onClick={() => { setMetronomeOn(m => !m); if (metronomeOn) setCount(0); }}
            className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-bold ${metronomeOn ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'}`}
            data-testid="cpr-metronome-toggle"
          >
            {metronomeOn ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
            {metronomeOn ? 'Pause' : 'Go'}
          </button>
          <div
            className={`w-8 h-8 rounded-full transition-all ${pulse ? 'scale-125' : 'scale-100'}`}
            style={{ backgroundColor: pulse ? '#EF4444' : '#7F1D1D', boxShadow: pulse ? '0 0 20px rgba(239,68,68,0.8)' : 'none' }}
          />
          <div className="text-center flex-1">
            <div className="text-xl font-mono font-bold tabular-nums leading-none">{count}</div>
            <div className="text-[9px] uppercase tracking-wider text-gray-400">{TARGET_BPM} BPM</div>
          </div>
          <button
            onClick={() => setSoundOn(s => !s)}
            className={`p-1.5 rounded ${soundOn ? 'bg-green-700' : 'bg-gray-700'}`}
            data-testid="cpr-metronome-sound-toggle"
          >
            {soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}
          </button>
        </div>
      ) : (
        <>
          {/* Tabs */}
          <div className="flex gap-2 p-3 border-b bg-gray-50">
            <Tab active={tab === 'adulte'} onClick={() => setTab('adulte')} icon={User} label="Adulte" age="≥ 8 ans" color="#DC2626" />
            <Tab active={tab === 'enfant'} onClick={() => setTab('enfant')} icon={Users} label="Enfant" age="1-8 ans" color="#F59E0B" />
            <Tab active={tab === 'bebe'} onClick={() => setTab('bebe')} icon={Baby} label="Bébé" age="< 1 an" color="#3B82F6" />
          </div>

          {/* Toggle ventilation */}
          <div className="px-4 py-2 flex flex-wrap items-center gap-2 bg-yellow-50 border-b border-yellow-200">
            <span className="text-xs font-semibold text-yellow-900">Type :</span>
            <button
              onClick={() => setWithBreaths(false)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-bold transition ${!withBreaths ? 'bg-red-600 text-white' : 'bg-white text-gray-600 border'}`}
              data-testid="cpr-mode-hands-only"
            >
              Compressions seulement
            </button>
            <button
              onClick={() => setWithBreaths(true)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-bold transition ${withBreaths ? 'bg-red-600 text-white' : 'bg-white text-gray-600 border'}`}
              data-testid="cpr-mode-with-breaths"
            >
              Avec ventilation (30:2)
            </button>
          </div>

          {/* Metronome bar */}
          <div className="px-4 py-3 flex items-center gap-3 bg-gray-900 text-white" data-testid="cpr-metronome">
            <button
              onClick={() => { setMetronomeOn(m => !m); if (metronomeOn) setCount(0); }}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg font-bold transition text-sm ${metronomeOn ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'}`}
              data-testid="cpr-metronome-toggle"
            >
              {metronomeOn ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              {metronomeOn ? 'Pause' : 'Lancer'}
            </button>

            <div className="flex items-center gap-3 flex-1 justify-center">
              <div
                className={`w-10 h-10 rounded-full transition-all ${pulse ? 'scale-125' : 'scale-100'}`}
                style={{ backgroundColor: pulse ? '#EF4444' : '#7F1D1D', boxShadow: pulse ? '0 0 25px rgba(239,68,68,0.8)' : 'none' }}
              />
              <div className="text-center">
                <div className="text-2xl font-mono font-bold tabular-nums leading-none">{count}</div>
                <div className="text-[9px] uppercase tracking-wider text-gray-400">compressions</div>
              </div>
              <div className="text-center px-2 border-l border-gray-700">
                <div className="text-lg font-bold leading-none">{TARGET_BPM}</div>
                <div className="text-[9px] uppercase tracking-wider text-gray-400">BPM</div>
              </div>
            </div>

            <button onClick={() => setCount(0)} className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600" title="Remettre à zéro" data-testid="cpr-metronome-reset">
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setSoundOn(s => !s)}
              className={`p-2 rounded-lg ${soundOn ? 'bg-green-700 hover:bg-green-600' : 'bg-gray-700 hover:bg-gray-600'}`}
              data-testid="cpr-metronome-sound-toggle"
            >
              {soundOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
            </button>
          </div>

          {/* Scrollable content */}
          <div className="overflow-y-auto flex-1 p-4">
            <Block title="1. Vérifier la victime" items={data.verifier} color="#7c3aed" icon={AlertTriangle} />
            <Block title="2. Position du citoyen" items={data.position} color="#0EA5E9" />
            <Block title={`3. ${techniqueTitle}`} items={technique} color="#DC2626" icon={HeartPulse} />

            <div className="my-4 p-3 rounded-lg bg-red-50 border-2 border-red-300">
              <p className="text-sm font-bold text-red-800 mb-1 flex items-center gap-1">
                🗣️ Ce que vous DITES au citoyen :
              </p>
              <div className="space-y-1.5 pl-1">
                {data.encouragement.map((q, i) => (
                  <div key={i} className="text-sm italic text-red-900 p-1.5 bg-white rounded border-l-2 border-red-400">
                    {q}
                  </div>
                ))}
              </div>
            </div>

            <Block title="4. Si un DEA est disponible" items={data.dea} color="#F59E0B" icon={Zap} />
            <Block title="5. NE PAS ARRÊTER tant que..." items={data.arret} color="#16a34a" />

            <div className="mt-5 p-3 rounded-lg bg-blue-50 text-xs text-blue-900">
              <strong>Référence :</strong> Lignes directrices Fondation des maladies du cœur et de l&apos;AVC du Canada — 2024.
              Compressions seulement pour citoyens non formés (recommandation primaire 911).
            </div>
          </div>
        </>
      )}

      {/* Resize grip — bottom-right corner (hidden when minimized) */}
      {!minimized && (
        <div
          onMouseDown={onResizeStart}
          onTouchStart={onResizeStart}
          className="absolute bottom-0 right-0 w-5 h-5 cursor-nwse-resize flex items-end justify-end p-0.5 text-gray-400 hover:text-red-500 z-10"
          title="Étirer le panneau"
          data-testid="cpr-guide-resize-handle"
          style={{
            background: 'linear-gradient(135deg, transparent 50%, rgba(220,38,38,0.15) 50%)',
            borderBottomRightRadius: '1rem',
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
            <path d="M11 1v10H1v-1h9V1h1zM7 5v6H1v-1h5V5h1z" />
          </svg>
        </div>
      )}
    </div>
  );
};

export default CPRQuickGuide;
